/**
 * Reusable WAF mitigation panel (Owl threat report) for one subdomain — used by
 * the Origin Shield "Firewall Rules" and "Access Control / Bot management" tabs.
 * Upper Summary: an attack-category donut + a metric grid over a FIXED set of
 * `categories` (`/waf/threat/diagram/summary`). Lower: Top IP / Top Country
 * sub-tabs → a top-N time-series chart (Summary line / Divide area) + a ranked,
 * sortable table (`/waf/threat/diagram/top`). The two tabs differ only by
 * `module` + `categories` + the `storageKey` used to persist mode/dimension.
 */
import { useMemo, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { PieChart } from '@vnetwork/ui/charts';
import { DataTable, Stat, StatGroup, type ColumnDef } from '@vnetwork/ui/data';
import { useWafThreatSummary, useWafThreatTopDiagram, type WafThreatDimension } from '@/api/waf';
import { ChartCard, RefreshButton } from '../../_shared/AnalyticsKit';
import { formatCount, formatNum } from '../../_shared/analytics-format';
import type { ResolvedRange } from './WafRangeControl';
import { intervalForRange, threatTopRows, threatTopRanking, type ChartMode } from './diagram-transform';
import { countryName } from '../_shared/geo';
import { loadJSON, saveJSON } from './storage';
import styles from './WafAnalytics.module.css';

/** Mitigation tabs are request-count views; the dropdown is line/area, not metric. */
const METRIC = 'request' as const;

/** Country code → full name; "00" is the vendor's unresolved-geo sentinel. */
const countryLabel = (c: string): string => (c === '00' ? 'Unidentified' : countryName(c));

type RankRow = { label: string; total: number };

export function WafMitigationPanel({
  serviceId,
  domain,
  range,
  module,
  categories,
  storageKey,
}: {
  serviceId: string;
  domain: string;
  range: ResolvedRange;
  /** Mitigation modules CSV (e.g. "Firewall rules,CRS rules"). */
  module: string;
  /** Fixed category labels for the donut + metric grid (matched against summary.labels). */
  categories: string[];
  /** localStorage prefix for this panel's persisted mode/dimension. */
  storageKey: string;
}) {
  const modeKey = `waf:${serviceId}:${storageKey}:mode`;
  const dimKey = `waf:${serviceId}:${storageKey}:dim`;

  const [mode, setMode] = useState<ChartMode>(() => loadJSON<ChartMode>(modeKey, 'summary'));
  const [dimension, setDimension] = useState<WafThreatDimension>(() =>
    loadJSON<WafThreatDimension>(dimKey, 'topIp'),
  );

  const hasScope = !!domain && !!range;
  const interval = range ? intervalForRange(range.start_time, range.end_time) : 86400;
  const filter = {
    domain,
    start_time: range?.start_time ?? 0,
    end_time: range?.end_time ?? 0,
    module,
    metric: METRIC,
  };

  const summaryQ = useWafThreatSummary(serviceId, filter, hasScope);
  const topQ = useWafThreatTopDiagram(serviceId, { ...filter, dimension, interval }, hasScope);

  // Donut + metric grid: pull the fixed categories from the summary by label.
  const categoryCount = (name: string) => {
    const i = summaryQ.data?.labels.indexOf(name) ?? -1;
    return i >= 0 ? Number(summaryQ.data?.datasets[i]) || 0 : 0;
  };
  const catValues = useMemo(
    () => categories.map((name) => ({ name, value: categoryCount(name) })),
    [summaryQ.data, categories],
  );
  const catTotal = catValues.reduce((a, c) => a + c.value, 0);
  const donutData = catValues.filter((c) => c.value > 0);
  // Odd category count → last stat spans both columns (5 → 2+2+1, 6 → 2+2+2).
  const lastSpans = categories.length % 2 === 1;

  // Top Country resolves codes to full names; Top IP keeps the raw address.
  const topLabelFor = dimension === 'topCountry' ? countryLabel : undefined;
  const { rows: topRows, series: topSeries } = useMemo(
    () => threatTopRows(topQ.data, interval, METRIC, topLabelFor),
    [topQ.data, interval, topLabelFor],
  );
  const ranking = useMemo(
    () => threatTopRanking(topQ.data, METRIC, topLabelFor),
    [topQ.data, topLabelFor],
  );
  const topTotal = ranking.reduce((a, r) => a + r.total, 0);

  // Show real skeletons while a query resolves — never the misleading "0"/empty fallback.
  const summaryLoading = !hasScope || summaryQ.isPending || summaryQ.isFetching;
  const topLoading = !hasScope || topQ.isPending || topQ.isFetching;

  const changeMode = (m: ChartMode) => {
    setMode(m);
    saveJSON(modeKey, m);
  };
  const changeDimension = (d: WafThreatDimension) => {
    setDimension(d);
    saveJSON(dimKey, d);
  };

  const isIp = dimension === 'topIp';
  const tableCols = useMemo<ColumnDef<RankRow, unknown>[]>(
    () => [
      {
        id: 'label',
        accessorKey: 'label',
        header: isIp ? 'IP address' : 'Country',
        cell: ({ row }) => (
          <span style={isIp ? { fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 13 } : undefined}>
            {row.original.label || '—'}
          </span>
        ),
      },
      {
        id: 'total',
        accessorKey: 'total',
        header: 'Requests',
        cell: ({ row }) => formatNum(row.original.total),
      },
    ],
    [isIp],
  );

  return (
    <>
      {/* Summary — attack-category donut + metric grid */}
      <Card>
        <CardHeader actions={<RefreshButton onRefresh={() => summaryQ.refetch()} />}>
          <CardTitle>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <ShieldAlert size={16} /> Summary
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className={styles.mitigationGrid}>
            {summaryLoading ? (
              <Skeleton style={{ height: 200, borderRadius: '50%', width: 200, justifySelf: 'center' }} />
            ) : (
              <PieChart
                donut
                height={200}
                data={donutData}
                showLegend
                valueFormatter={(v) => formatNum(Number(v))}
                centerLabel={
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{formatNum(catTotal)}</div>
                    <div style={{ fontSize: 12, color: 'var(--vn-fg-muted)' }}>requests</div>
                  </div>
                }
              />
            )}
            {summaryLoading ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-4)' }}>
                {categories.map((_, i) => (
                  <Skeleton
                    key={i}
                    style={{ height: 56, ...(lastSpans && i === categories.length - 1 ? { gridColumn: 'span 2' } : null) }}
                  />
                ))}
              </div>
            ) : (
              <StatGroup columns={2}>
                {catValues.map((c, i) =>
                  lastSpans && i === catValues.length - 1 ? (
                    <div key={c.name} style={{ gridColumn: 'span 2' }}>
                      <Stat label={c.name} value={formatNum(c.value)} />
                    </div>
                  ) : (
                    <Stat key={c.name} label={c.name} value={formatNum(c.value)} />
                  ),
                )}
              </StatGroup>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Detailed attack analytics — Top IP / Top Country */}
      <div>
        <Tabs value={dimension} onValueChange={(v) => changeDimension(v as WafThreatDimension)}>
          <TabsList>
            <TabsTrigger value="topIp">Top IP</TabsTrigger>
            <TabsTrigger value="topCountry">Top Country</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className={styles.controls} style={{ marginTop: 'var(--vn-space-3)' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--vn-space-2)',
              color: 'var(--vn-fg-muted)',
              fontSize: 'var(--vn-font-size-sm)',
            }}
          >
            <ShieldAlert size={14} /> Total requests: {topLoading ? '—' : formatNum(topTotal)}
          </span>
          <div className={styles.controlsRight}>
            <Select value={mode} onValueChange={(v) => changeMode(v as ChartMode)}>
              <SelectTrigger aria-label="Chart mode" className={styles.modeSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Summary</SelectItem>
                <SelectItem value="divide">Divide</SelectItem>
              </SelectContent>
            </Select>
            <RefreshButton onRefresh={() => topQ.refetch()} />
          </div>
        </div>

        <div
          className={mode === 'divide' ? styles.denseFill : undefined}
          style={{ marginTop: 'var(--vn-space-3)' }}
        >
          <ChartCard
            title={isIp ? 'Top IPs over time' : 'Top countries over time'}
            data={topRows}
            xKey="t"
            series={topSeries}
            type={mode === 'divide' ? 'area' : 'line'}
            height={280}
            stacked={mode === 'divide'}
            showLegend
            valueFormatter={(v) => formatCount(Number(v))}
            loading={topLoading}
          />
        </div>

        <div style={{ marginTop: 'var(--vn-space-4)' }}>
          {topLoading ? (
            <Skeleton style={{ height: 240 }} />
          ) : (
            <DataTable
              columns={tableCols}
              data={ranking}
              getRowId={(r) => r.label}
              emptyState="No mitigation activity in the selected range."
            />
          )}
        </div>
      </div>
    </>
  );
}

export default WafMitigationPanel;
